import React, { useState, useEffect, useRef } from 'react';
import { useShowStore } from '../../stores/showStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useClockStore } from '../../stores/clockStore';
import CueTypeBadge from '../rundown/CueTypeBadge';

function formatDurDisplay(sec) {
  if (sec === null || sec === undefined) return '--:--';
  const abs = Math.abs(sec);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  const sign = sec < 0 ? '-' : '+';
  if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${sign}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTime(isoString) {
  const d = isoString ? new Date(isoString) : new Date();
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// Determine if script content is non-empty (handles HTML and plain text)
function hasScriptContent(script) {
  if (!script) return false;
  const stripped = script.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, '').trim();
  return stripped.length > 0;
}

// Self-updating cue countdown clock
function CueCountdown({ plannedDuration, onAirStartTime }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!onAirStartTime) return;
    const tick = () => setElapsed(Math.round((Date.now() - onAirStartTime) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [onAirStartTime]);

  const remaining = (plannedDuration || 0) - elapsed;
  const isOver = remaining < 0;
  const cls = isOver
    ? 'text-red-400 text-xl font-mono'
    : remaining < 30
    ? 'text-amber-400 text-xl font-mono'
    : 'text-green-400 text-xl font-mono';

  return (
    <div className="text-center">
      <div className="text-xs text-gray-700 uppercase tracking-wide">Cue +/-</div>
      <div className={cls}>{formatDurDisplay(remaining)}</div>
    </div>
  );
}

export default function SoloView({ showId, show }) {
  const onAirCue = useShowStore(s => s.cues.find(c => c.status === 'on_air') || null);
  const nextCue = useShowStore(s => {
    const idx = s.cues.findIndex(c => c.status === 'on_air');
    return s.cues.slice(idx + 1).find(c => c.status === 'pending') || s.cues.find(c => c.status === 'pending') || null;
  });
  const cues = useShowStore(s => s.cues);
  const onAirStartTime = useShowStore(s => s.onAirStartTime);
  const clockData = useClockStore(s => s.clockData);

  // Flash when on-air cue changes and has script content
  const [flashing, setFlashing] = useState(false);
  const prevOnAirId = useRef(null);
  useEffect(() => {
    if (onAirCue?.id !== prevOnAirId.current) {
      prevOnAirId.current = onAirCue?.id;
      if (hasScriptContent(onAirCue?.script)) {
        setFlashing(true);
        const t = setTimeout(() => setFlashing(false), 1600);
        return () => clearTimeout(t);
      }
    }
  }, [onAirCue?.id]);

  return (
    <div className="h-full flex flex-col bg-app-deep p-4 gap-3 relative">
      {/* Background-only flash layer */}
      {flashing && <div className="absolute inset-0 commentary-flash pointer-events-none z-0" />}

      {/* Clock strip */}
      <div className="relative z-10 flex items-center justify-between text-sm font-mono flex-shrink-0">
        <span className="text-gray-500 truncate max-w-xs">{show?.name}</span>
        <div className="flex items-center gap-8">
          <div className="text-center">
            <div className="text-xs text-gray-700 uppercase tracking-wide">Time</div>
            <div className="text-app-fg text-xl">{formatTime(clockData.wallClock)}</div>
          </div>
          {onAirCue && (
            <CueCountdown
              plannedDuration={onAirCue.planned_duration}
              onAirStartTime={onAirStartTime}
            />
          )}
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
          show?.status === 'live' ? 'text-red-400 bg-red-900/30 on-air-pulse' : 'text-gray-600 bg-gray-800'
        }`}>
          {show?.status || 'draft'}
        </span>
      </div>

      {/* Main stacked layout — On Air top, Next bottom */}
      <div className="relative z-10 flex-1 flex flex-col gap-3 min-h-0">

        {/* TOP — On Air: label + full script */}
        <div className="flex-[3] flex flex-col border border-red-800 bg-red-950/20 rounded-lg overflow-hidden min-h-0">
          <div className="px-6 py-3 border-b border-red-800/40 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-bold uppercase tracking-widest text-red-400/70">On Air</span>
            {onAirCue && <CueTypeBadge type={onAirCue.cue_type} compact />}
          </div>
          {onAirCue ? (
            <div className="flex-1 overflow-y-auto p-6">
              <h2 className="text-4xl font-bold text-app-fg mb-5 leading-tight">{onAirCue.label}</h2>
              {hasScriptContent(onAirCue.script) && (
                <div
                  className="tiptap-content text-xl text-gray-200 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: onAirCue.script }}
                />
              )}
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <span className="text-gray-700">—</span>
            </div>
          )}
        </div>

        {/* BOTTOM — Next: single line label only */}
        <div className="flex-shrink-0 flex items-center border border-amber-800/40 bg-amber-950/10 rounded-lg overflow-hidden px-6 py-3 gap-4">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-400/60 flex-shrink-0">Next</span>
          {nextCue ? (
            <>
              <CueTypeBadge type={nextCue.cue_type} compact />
              <span className="text-2xl font-bold text-amber-100 truncate">{nextCue.label}</span>
            </>
          ) : (
            <span className="text-gray-700 text-sm">—</span>
          )}
        </div>
      </div>

      {/* Upcoming strip */}
      <div className="relative z-10 h-14 bg-app-sub border border-app-surface rounded-lg px-4 flex items-center gap-3 overflow-x-auto flex-shrink-0">
        {cues
          .filter(c => c.status === 'pending' && c.id !== nextCue?.id)
          .slice(0, 8)
          .map((c, i) => (
            <div
              key={c.id}
              className="flex-shrink-0 flex items-center gap-2 px-3 py-1.5 bg-app-header border border-app-border rounded text-xs"
            >
              <span className="text-gray-600 font-mono">{i + 2}</span>
              <CueTypeBadge type={c.cue_type} compact />
              <span className="text-gray-400 max-w-24 truncate">{c.label}</span>
            </div>
          ))
        }
        {cues.filter(c => c.status === 'pending').length === 0 && (
          <span className="text-gray-700 text-xs">No pending cues</span>
        )}
      </div>
    </div>
  );
}
